import React, {  useEffect, useState } from 'react';
import { Grid, Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { 
  FieldErrors, 
  UseFormClearErrors, 
  UseFormRegister, 
  UseFormSetError, 
  UseFormSetValue, 
  UseFormTrigger, 
  UseFormWatch 
} from "react-hook-form";
import { FormTypes } from '../Types';
import FormFieldRenderer from './FormFieldRenderer';
import { fetchDropdownValues } from '../api/apiFunctions';
import CreationUpdateDetails from './CreationUpdateDetails';

// Move fetchDropdownValues to EditForm


interface EditFormProps {
  formFields: any[];
  control: any;
  watch: UseFormWatch<FormTypes>;
  isNotDraftState: boolean;
  errors: FieldErrors<FormTypes>;
  register: UseFormRegister<any>;
  trigger: UseFormTrigger<FormTypes>;
  clearErrors: UseFormClearErrors<FormTypes>;
  setError: UseFormSetError<FormTypes>;
  setValue: UseFormSetValue<FormTypes>; 
  intialformData: any;
  formMode: 'view' | 'edit' | 'add';
  onCancel: () => void;
  onSave: () => void;
  /** When set, shows a second primary action (e.g. save and return to list). */
  onSaveAndClose?: () => void;
  isFormModified: any;
  isFormFlag?: boolean;
  processJudge?:string;
  processAssociationId?:string;
  xevmpdId?:string;
  details?:any;
  recordID?:string;
}

const EditForm: React.FC<EditFormProps> = ({
  formFields,
  control,
  watch,
  isNotDraftState,
  errors,
  trigger,
  register,
  clearErrors,
  setError,
  setValue,
  intialformData,
  formMode,
  onCancel,
  onSave,
  onSaveAndClose,
  isFormModified,
  isFormFlag,
  processJudge,
  processAssociationId,
  xevmpdId,
  details,
  recordID
}) => {
  const { t } = useTranslation();

  
  // State to store async options for each field
  const [asyncOptions, setAsyncOptions] = useState<Record<string, { label: string; value: string }[]>>({});
  const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>({});
  
  // Find first editable field for autofocus
  const firstEditableFieldName = "";

  // Function to fetch async options for a specific field
  const fetchFieldOptions = async (formField: any) => {
    const { field, asyncConfig } = formField;
    
    if (!asyncConfig?.url) return;
    
    try {
      // Set loading state for this field
      setLoadingFields(prev => ({ ...prev, [field]: true }));
      
      const data = await fetchDropdownValues(asyncConfig.url);
      const transformedOptions = data.map((item: any) => ({
        label: item[asyncConfig.labelPath],
        value: item[asyncConfig.valuePath]
      }));
      
      // Store the options for this field
      setAsyncOptions(prev => ({
        ...prev,
        [field]: transformedOptions
      }));
    } catch (error) {
      console.error(`Failed to fetch async options for ${field}:`, error);
      // Set empty options on error
      setAsyncOptions(prev => ({
        ...prev,
        [field]: []
      }));
    } finally {
      // Clear loading state
      setLoadingFields(prev => ({ ...prev, [field]: false }));
    }
  };

  // Fetch async options for all fields on mount
  useEffect(() => {
    const fetchAllAsyncOptions = async () => {
      const asyncFields = formFields.filter(field => 
        (field.type === "select" || field.type === "radio") && 
        field.asyncConfig?.url
      );
      
      // Fetch options for each async field
      await Promise.all(asyncFields.map(fetchFieldOptions));
    };
    
    if (formFields?.length > 0) {
      fetchAllAsyncOptions();
    }
  }, [formFields]);

  // Function to get options for a specific field
  const getFieldOptions = (formField: any) => {
    const { field, type, asyncConfig, options: staticOptions } = formField;
    
    // If it's a select or radio field with async config
    if ((type === "select" || type === "radio") && asyncConfig?.url) {
      // Return async options if they exist, otherwise return empty array
      return asyncOptions[field] || [];
    }
    
    // For non-async fields, return the static options
    return staticOptions || [];
  };

  // Function to check if a field is loading async options
  const isFieldLoading = (formField: any) => {
    const { field, asyncConfig } = formField;
    return asyncConfig?.url ? loadingFields[field] || false : false;
  };

  return (
    <Box>
      <form>
        <Grid container>
          {Array.isArray(formFields) && formFields.map((formField: any) => {
            // Get options based on field type (async or static)
            const options = getFieldOptions(formField);
            const isLoading = isFieldLoading(formField);
            
            return (
              <Grid
                item
                xs={formField?.field_xs}
                md={formField?.field_md}
                xl={formField?.field_xl}
                lg={formField?.field_lg}
                sx={{ mb: 3 }}
                key={formField?.field}
              >
                {/* Pass loading state and options to FormFieldRenderer */}
                <FormFieldRenderer
                  formField={{
                    ...formField,
                    isLoading: isLoading
                  }}
                  control={control}
                  watch={watch}
                  register={register}
                  isNotDraftState={isNotDraftState}
                  errors={errors}
                  trigger={trigger}
                  clearErrors={clearErrors}
                  setError={setError}
                  setValue={setValue}
                  intialformData={{...intialformData, recordID: recordID}}
                  formMode={formMode}
                  options={options}
                  firstEditableFieldName={firstEditableFieldName}
                />
              </Grid>
            );
          })}
          {(formMode === 'edit' || formMode === "add") && (
            
            <Grid item xs={12}>
              
              
              <Box display="flex" justifyContent="flex-end" mt={2} flexDirection="column" alignItems="flex-end">
                {/* Message above the buttons */}
                {isFormModified && formMode !== "add" && (
                  <div style={{ 
                    fontSize: '12px', 
                    color: 'grey', 
                    marginBottom: '8px',
                    textAlign: 'right',
                  }}>
                    {t("noDataUpdatedMessage")}
                  </div>
                )}
                
                {/* Buttons row */}
                <Box display="flex" gap={1}>
                  {!isFormFlag &&<Button onClick={onCancel} color="primary">
                    {t("cancel")}
                  </Button>}
                  <Button onClick={onSave} variant="contained" color="primary">
                    {t("save")}
                  </Button>
                  {onSaveAndClose ? (
                    <Button
                      onClick={onSaveAndClose}
                      variant="contained"
                      color="primary"
                    >
                      {t("saveAndClose")}
                    </Button>
                  ) : null}
                  {/* {!!processAssociationId && processJudge === "IN_PROGRESS" && formMode==="edit" && (<ProcessButton  ref={processButtonRef}
                      taskButtonConfig={taskButtonConfig} xevmpdId={xevmpdId}></ProcessButton>)} */}
                </Box>
              </Box>
              <CreationUpdateDetails details={details}></CreationUpdateDetails>
            </Grid>
          )}
        </Grid>
      </form>
    </Box>
  );
};

export default EditForm;